import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '../api'

/**
 * Service de Mentores — instrutores das masterclasses.
 * Cadastro feito pelo super_admin via `/super-admin → Mentores`.
 */
export interface Mentor {
  id: string
  name: string
  photo: string | null
  role: string | null
  company: string | null
  bio: string | null
  linkedin: string | null
  instagram: string | null
  active: boolean
  createdAt: string
  updatedAt: string
  createdBy: string | null
}

export interface CreateMentorDTO {
  name: string
  photo?: string | null
  role?: string | null
  company?: string | null
  bio?: string | null
  linkedin?: string | null
  instagram?: string | null
}

export interface UpdateMentorDTO extends Partial<CreateMentorDTO> {
  active?: boolean
}

const MENTORS_KEY = ['super-admin', 'mentors']

export function useMentors(opts: { activeOnly?: boolean; enabled?: boolean } = {}) {
  const { activeOnly = true, enabled = true } = opts
  return useQuery<Mentor[]>({
    queryKey: [...MENTORS_KEY, { activeOnly }],
    queryFn: async () => {
      const { data } = await api.get<Mentor[]>('/admin/mentors', {
        params: { activeOnly: String(activeOnly) },
      })
      return data
    },
    enabled,
    staleTime: 5 * 60 * 1000,
  })
}

export function useMentor(id: string | null) {
  return useQuery<Mentor>({
    queryKey: [...MENTORS_KEY, id],
    queryFn: async () => {
      const { data } = await api.get<Mentor>(`/admin/mentors/${id}`)
      return data
    },
    enabled: !!id,
  })
}

export function useCreateMentor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (dto: CreateMentorDTO) => {
      const { data } = await api.post<Mentor>('/admin/mentors', dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MENTORS_KEY })
    },
  })
}

export function useUpdateMentor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...dto }: UpdateMentorDTO & { id: string }) => {
      const { data } = await api.put<Mentor>(`/admin/mentors/${id}`, dto)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MENTORS_KEY })
    },
  })
}

export function useDeactivateMentor() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.delete<Mentor>(`/admin/mentors/${id}`)
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: MENTORS_KEY })
    },
  })
}
